import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { useAuth, useOrganization } from '@clerk/clerk-react';

import { DepartmentContext } from './context';

import type { Department, DepartmentContextValue } from './types';

interface DepartmentProviderProps {
  children: ReactNode;
}

export const DepartmentProvider: React.FC<DepartmentProviderProps> = ({
  children,
}) => {
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const { isLoaded: isOrgLoaded, organization, membership } = useOrganization();

  const [userDepartments, setUserDepartments] = useState<Department[]>([]);
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const processDepartmentData = useCallback(() => {
    console.log('DepartmentProvider: Processing data...', {
      isOrgLoaded,
      isSignedIn,
      org: !!organization,
      mem: !!membership,
    });
    if (!isOrgLoaded || !isSignedIn || !organization || !membership) {
      if (!isOrgLoaded) setIsLoading(true);
      else {
        setUserDepartments([]);
        setSelectedDeptId(null);
        setError(null);
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userDeptIds = (membership.publicMetadata?.deptIds ||
        []) as string[];
      const allDeptsData = (organization.publicMetadata?.departments ||
        {}) as Record<string, Omit<Department, 'id'>>;

      const departmentsArray: Department[] = Object.entries(allDeptsData)
        .map(
          ([id, data]): Department => ({
            id,
            name: data?.name || `未命名 (${id})`,
            address: data?.address,
            phone: data?.phone,
            fax: data?.fax,
            postal_code: data?.postal_code,
          })
        )
        .filter((dept) => userDeptIds.includes(dept.id));

      setUserDepartments(departmentsArray);

      const currentSelectionValid = departmentsArray.some(
        (dept) => dept.id === selectedDeptId
      );
      if (
        departmentsArray.length > 0 &&
        (!selectedDeptId || !currentSelectionValid)
      ) {
        setSelectedDeptId(departmentsArray[0].id);
      } else if (departmentsArray.length === 0) {
        setSelectedDeptId(null);
      }
    } catch (err: unknown) {
      console.error('DepartmentProvider: Error processing data:', err);
      const errorMessage = err instanceof Error ? err.message : '不明なエラー';
      setError(`部署情報の処理中にエラーが発生しました: ${errorMessage}`);
      setUserDepartments([]);
      setSelectedDeptId(null);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, membership, isOrgLoaded, isSignedIn]); // selectedDeptId を除外

  useEffect(() => {
    if (isAuthLoaded) processDepartmentData();
    else setIsLoading(true);
  }, [isAuthLoaded, processDepartmentData]);

  const selectDepartment = useCallback(
    (deptId: string | null) => {
      if (
        deptId === null ||
        userDepartments.some((dept) => dept.id === deptId)
      ) {
        setSelectedDeptId(deptId);
      } else {
        console.warn(`DepartmentProvider: Invalid deptId selected: ${deptId}`);
      }
    },
    [userDepartments]
  );

  const value: DepartmentContextValue = {
    userDepartments,
    selectedDeptId,
    isLoading,
    error,
    selectDepartment,
    selectedDepartment:
      userDepartments.find((dept) => dept.id === selectedDeptId) || null,
  };

  return React.createElement(
    DepartmentContext.Provider,
    { value: value },
    children
  );
};
